import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import get from 'lodash/get';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';

import { theme } from 'theme';

import { downloadService, uploadPortfolio, confirmPortfolio } from 'services';
import { portfolioUploadSteps as steps } from 'shared/constants/portfolioUploadSteps';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { portfolioUploadSchema, creditReviewCreatePortfolioSchema } from 'shared/validations';
import { useNotifications } from 'shared/hooks';
import { Loader } from 'ui/Loader/Loader';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { SIGUpdateDossier } from 'services/dossier/dossierService';
import { generateCSV } from 'utils';
import { getAuthDataOrganisationId } from 'selectors/auth';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { getDossiersAction, refreshDossiersAction } from 'store/portfolio/dossierActions';
import { formatNumber } from 'shared/functions';

const useStyles = makeStyles({
  downloadTemplateContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  downloadButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  input: {
    display: 'none',
  },
  uploadContainer: {
    padding: '2px 4px',
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
  },
  fileUploadText: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  summaryContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  checkIcon: {
    fontSize: 80,
  },
  selectButton: {
    width: 150,
    height: 100,
    textTransform: 'inherit',
    border: 'none',
    color: theme.palette.text.primary,
    boxShadow: '0 4px 4px 2px rgba(155, 155, 155, 0.3)',
    padding: '0 35px',
  },
  selectTitle: {
    fontSize: 15,
  },
});

const PortfolioUploadCreditReview = ({
  onClose,
  backToTypeSelection,
  onClickCreateNew,
  createdPortfolioName,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { notifyError } = useNotifications();

  const organisationId = useSelector(getAuthDataOrganisationId);

  const { data: dossiersData = [], isPending: isDossiersPending, shouldRefresh } = useSelector(
    ({ dossier }) => dossier.byRatingType[ratingTypes.CREDIT_REVIEW],
  );

  const [fileUploadInputRef, setFileUploadInputRef] = useState(null);

  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(steps.UPLOAD);
  const [summaryStep, setSummaryStep] = useState(0);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [tempDossierId, setTempDossierId] = useState();
  const [dossiers, setDossiers] = useState([]);

  useEffect(() => {
    setDossiers(
      dossiersData &&
        dossiersData
          .map(([, [id, name], , , , [, , isDedicated]]) => ({
            id,
            name,
            isDedicated,
          }))
          .filter(dossier => !dossier.isDedicated),
    );
  }, [dossiersData]);

  const didUnmount = React.useRef(false);

  useEffect(() => {
    dispatch(
      getDossiersAction({ ratingType: ratingTypes.CREDIT_REVIEW, requestParams: { limit: 100 } }),
    );
    return () => {
      didUnmount.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh]);

  useEffect(() => {
    if (error) {
      notifyError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const ignoredRowsCount = 1; // Ignore first row as it contains just headers
  const errorsNumber = data && data.errors.length && data.errors.length - ignoredRowsCount;
  const total = data ? data.total : 0;

  const summarySteps = [
    {
      label: t('Step one'),
      text: errorsNumber
        ? t(
            'Could not be identified from the file. Please review and amend the file. If you continue, the items listed below will be excluded from the upload.',
          )
        : t('Could be identified from the file.'),
      ratingsCount: errorsNumber,
      customField: errorsNumber ? (
        <Typography className={classes.textCenter}>
          <Button
            component="a"
            href={generateCSV(data.errors)}
            download="portfolio-errors-list.csv"
            variant="contained"
            color="secondary"
          >
            {t('Download a list of unidentifiable elements')}
          </Button>
        </Typography>
      ) : (
        undefined
      ),
    },
    {
      label: t('Step two'),
      text: t('Have already been requested by a user from your organisation.'),
      ratingsCount: get(data, 'already_requested', 0),
    },
    {
      label: t('Step three'),
      text: t('Will be added to the Dossier you’ve selected.'),
      ratingsCount: get(data, 'added_to_dossier', 0),
    },
  ];

  const sampleTemplateUrl = '/esg/api/upload-portfolio/?type=SCORE%20REVIEW';
  const fileUploadInputRefChanged = element => setFileUploadInputRef(element);

  const placeOrders = async () => {
    setIsPending(true);

    try {
      await SIGUpdateDossier({
        dossier: selectedDossier,
        dossierId: tempDossierId,
      });

      setStep(steps.FINISH);
    } catch (e) {
      setError('Failed to finish portfolio upload');
    }
    setIsPending(false);
  };

  const handleStepSubmit = async payload => {
    if (step === steps.UPLOAD) {
      setIsPending(true);
      try {
        const uploadData = await uploadPortfolio({
          portfolio: payload && payload.fileUploadInput,
          type: portfolioTypes.CREDIT_REVIEW,
          organisationId,
        });
        setData({ ...uploadData.result, fileName: payload.fileUploadInput.name });
        setTempDossierId(uploadData.id);
        setStep(steps.CREATE_DOSSIER);
      } catch (e) {
        setError(e.message);
      }
      setIsPending(false);
    } else if (step === steps.CREATE_DOSSIER) {
      setStep(steps.SUMMARY);
    }
  };

  useEffect(() => {
    if (createdPortfolioName && step === steps.CREATE_DOSSIER) {
      setSelectedDossier(createdPortfolioName);
      handleStepSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdPortfolioName]);

  const onSelectDossier = (e, defaultAction) => {
    setSelectedDossier(e.target.value);
    if (defaultAction) {
      defaultAction(e);
    }
  };

  const downloadErrorHandler = () => {
    notifyError(t('Failed to download sample portfolio template'));
  };

  const closeModal = () => {
    setSummaryStep(0);
    onClose();
  };

  const handleDownloadTemplateClick = () =>
    downloadService({
      path: sampleTemplateUrl,
      name: 'portfolio-template.csv',
      onError: downloadErrorHandler,
    });

  const handleDone = async () => {
    setIsPending(true);
    try {
      await confirmPortfolio(tempDossierId);
      dispatch(
        refreshDossiersAction({
          reason: refreshReasons.UPDATED,
          ratingType: ratingTypes.CREDIT_REVIEW,
        }),
      );
    } catch (e) {
      setError(e.message);
    }
    setIsPending(false);
    closeModal();
  };

  const previousStep = () => {
    switch (step) {
      case steps.UPLOAD:
        backToTypeSelection();
        break;
      case steps.SUMMARY:
        if (summaryStep > 0) {
          setSummaryStep(summaryStep - 1);
        } else {
          setStep(step - 1);
        }
        break;
      default:
        setStep(step - 1);
    }
  };

  const nextSummaryStep = () => {
    if (summaryStep < summarySteps.length - 1) {
      setSummaryStep(summaryStep + 1);
    } else {
      placeOrders();
    }
  };

  const updateUploadBoxData = (setFieldValueFunc, file, text) => {
    if (!file && fileUploadInputRef) {
      fileUploadInputRef.value = null;
    }
    setFieldValueFunc('fileUploadInput', file);
    setFieldValueFunc('fileUploadText', text);
  };

  const stepUploadInitialValues = {
    description: '',
    origin: window.location.href,
    fileUploadText: '',
    fileUploadInput: null,
  };

  const stepCreateDossierInitialValues = {
    dossierId: '',
  };

  const stepUpload = (
    <Formik
      key={steps.UPLOAD}
      initialValues={stepUploadInitialValues}
      onSubmit={payload => handleStepSubmit(payload)}
      validationSchema={portfolioUploadSchema(['csv', 'xls', 'xlsx'])}
    >
      {({ values, errors, touched, setFieldValue }) => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>
                  {t(
                    `The upload of your portfolio will create a portfolio for the entities that 
                     have been identified from the upload file.`,
                  )}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography>
                  {t(
                    `To prepare the upload, please create a list of entities, using the template below. 
                     To finalise the portfolio upload, please follow the steps in the upload wizard below.`,
                  )}
                </Typography>
              </Box>
              <Divider />
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography>{`${t('File format')}: .csv, .xls, .xlsx`}</Typography>
                  <Typography>{`${t('UPLOAD.MAX_SIZE')}: 100 ${t(
                    'ENTITIES',
                  ).toLowerCase()}`}</Typography>
                  <Typography>{`${t(
                    'Column heading',
                  )}: EASYNUMBER, NAME, COUNTRY ISO 3 CODE, POSTCODE, VAT`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={handleDownloadTemplateClick}
                    variant="outlined"
                    disabled={isPending || isDossiersPending}
                  >
                    {t('DOWNLOAD_TEMPLATE')}
                  </Button>
                </Box>
              </div>
              <Divider />
              <ErrorMessage name="fileUploadInput">
                {message => (
                  <Box mt={2}>
                    <Typography color="error">{message}</Typography>
                  </Box>
                )}
              </ErrorMessage>
              <Paper className={classes.uploadContainer}>
                <input
                  className={classes.input}
                  id="file-upload"
                  name="file-upload"
                  ref={fileUploadInputRefChanged}
                  type="file"
                  onChange={event => {
                    updateUploadBoxData(
                      setFieldValue,
                      event.currentTarget.files[0],
                      get(event.currentTarget.files, '[0].name', ''),
                    );
                  }}
                />
                <Field name="fileUploadText">
                  {({ field }) => (
                    <InputBase
                      {...field}
                      className={classes.fileUploadText}
                      placeholder={t('UPLOAD.NO_FILE_SELECTED')}
                      error={errors.fileUploadInput && touched.fileUploadText}
                      readOnly
                    />
                  )}
                </Field>
                <IconButton
                  type="submit"
                  disabled={!fileUploadInputRef || !fileUploadInputRef.value}
                  onClick={() => {
                    updateUploadBoxData(setFieldValue, null, '');
                  }}
                  size="large">
                  <DeleteIcon />
                </IconButton>
                <label htmlFor="file-upload">
                  <Button variant="contained" color="primary" component="span">
                    {t('UPLOAD.BROWSE')}
                  </Button>
                </label>
              </Paper>
            </DialogContent>
            <DialogActions>
              {step !== steps.FINISH && (
                <Box>
                  <Button variant="outlined" onClick={previousStep}>
                    {t('Previous')}
                  </Button>
                </Box>
              )}
              <div style={{ flex: '1 0 0' }} />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || isDossiersPending || !get(values, 'fileUploadInput')}
              >
                {t('Next')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const stepCreateDossier = (
    <Formik
      key={steps.CREATE_DOSSIER}
      className={classes.fullWidth}
      initialValues={stepCreateDossierInitialValues}
      onSubmit={payload => handleStepSubmit(payload)}
      validationSchema={creditReviewCreatePortfolioSchema}
    >
      {() => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>{t('The file selected below will be uploaded.')}</Typography>
              </Box>
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography color="primary">{`${data.fileName}`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={() => setStep(steps.UPLOAD)}
                    variant="contained"
                    color="primary"
                    disabled={isPending || isDossiersPending}
                  >
                    {t('Change File')}
                  </Button>
                </Box>
              </div>
              <Box mt={2}>
                <Typography>{t('Create a portfolio to add the above file')}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {!!dossiers.length && (
                  <Field name="dossierId">
                    {({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Select dossier"
                        fullWidth
                        onChange={e => onSelectDossier(e, field.onChange)}
                      >
                        {dossiers.map(({ id, name }) => (
                          <MenuItem key={id} value={id}>
                            {name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Box>
                <Button variant="outlined" onClick={previousStep}>
                  {t('Previous')}
                </Button>
              </Box>
              <div style={{ flex: '1 0 0' }} />
              <Button variant="outlined" onClick={onClickCreateNew}>
                {t('Create new portfolio')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || isDossiersPending || !selectedDossier}
              >
                {t('Next')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const stepSummary = (
    <>
      <DialogContent>
        <Box mb={2}>
          <Stepper activeStep={summaryStep}>
            {summarySteps.map(item => {
              return (
                <Step key={item.label}>
                  <StepLabel>{item.label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        <Box mb={2} className={classes.summaryContent}>
          <Typography color="primary" variant="h3">
            {errorsNumber ? summarySteps[summaryStep].ratingsCount : total}
          </Typography>
          <Box p={2}>
            <Typography variant="h6">{t('out of')}</Typography>
          </Box>
          <Typography variant="h3">{formatNumber(total)}</Typography>
        </Box>
        <Box p={2} mb={2}>
          <Typography variant="body1" className={classes.textCenter}>
            {summarySteps[summaryStep].text}
          </Typography>
        </Box>
        {summarySteps[summaryStep].customField ? (
          <Box p={2}>{summarySteps[summaryStep].customField}</Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        {step !== steps.FINISH && (
          <Box>
            <Button variant="outlined" onClick={previousStep}>
              {t('Previous')}
            </Button>
          </Box>
        )}
        <div style={{ flex: '1 0 0' }} />
        <Button
          variant="contained"
          color="primary"
          disabled={isPending || isDossiersPending}
          onClick={nextSummaryStep}
        >
          {t('Next')}
        </Button>
      </DialogActions>
    </>
  );

  const stepFinish = (
    <>
      <DialogContent className={classes.textCenter}>
        <Box
          width={140}
          height={140}
          borderRadius="50%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginY={2}
          marginX="auto"
          boxShadow="0 3px 4px 1px rgba(155, 155, 155, 0.3)"
          bgcolor={theme.palette.custom.verySoftCyan}
        >
          <CheckIcon className={classes.checkIcon} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          disabled={isPending || isDossiersPending}
          onClick={handleDone}
        >
          {t('Done')}
        </Button>
      </DialogActions>
    </>
  );

  const stepsMapping = {
    [steps.UPLOAD]: stepUpload,
    [steps.CREATE_DOSSIER]: stepCreateDossier,
    [steps.SUMMARY]: stepSummary,
    [steps.FINISH]: stepFinish,
  };

  return (
    <>
      {stepsMapping[step]}
      {(isPending || isDossiersPending) && <Loader cover />}
    </>
  );
};

PortfolioUploadCreditReview.defaultProps = {
  onClose: () => null,
  backToTypeSelection: () => null,
  onClickCreateNew: () => null,
  createdPortfolioName: undefined,
};

PortfolioUploadCreditReview.propTypes = {
  onClose: PropTypes.func,
  onClickCreateNew: PropTypes.func,
  createdPortfolioName: PropTypes.string,
  backToTypeSelection: PropTypes.func,
};

export default PortfolioUploadCreditReview;
